import React, { CSSProperties } from 'react'
import { EntityObject } from './geoAutocompletion-types'
import { GeocodeObject } from './geographicInformationSystem-types'
import { UploadFile } from './multimedia-types'
import {
  AreaCoverageOption,
  ConstructibleAfterOption,
  ConstructionConditionOption,
  ConstructionPhaseOption,
  EfficiencyClassOption,
  EnergySourceOption,
  FlooringTypeOption,
  HeatingTypeOption,
  LocationClassificationTypeOption,
  ParkingSpaceTypeOption,
  SupplyTypeOption,
  UsageTypeOption,
} from './dropdown-types'
import { EnergyCertificateAvailability } from './radio-types'
import { AbstractScreenValidationResult, ValidationResult } from './validation-types'
import { FlowInformation } from './flow-types'
import { ScreenName } from '../services/domain/wizardScreens'
import { Path } from './helper-types'
import { MapImages } from './real-estate-api-types'

//see https://stackoverflow.com/questions/69327990/how-can-i-make-one-property-non-optional-in-a-typescript-type
export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

export type InputType = 'text' | 'number' | 'email' | 'tel'

export type EntryMode =
  | 'VDE_INSERTION'
  | 'OBJECT_INSERTION'
  | 'NEW_INSERTION'
  | 'EDIT_FROM_OXM'
  | 'EDIT_FROM_OBJECT_CHOOSER'
  | 'EDIT_FROM_MY_SCOUT'
  | 'EDIT_FOR_ENERGY_CERTIFICATE'
  | 'EDIT_FROM_VDE'
  | 'EDIT_FROM_HOMEOWNER_WORLD'
  | string

export function isEditEntryMode(entryMode?: EntryMode) {
  return entryMode && entryMode.startsWith('EDIT_')
}

export interface OtherCostsInterface {
  additionalCosts?: number
  deposit?: string
  heatingCosts?: string
  heatingCostsOption?: boolean
  manualTotalRent?: string
}

export interface ContentStateInterface extends OtherCostsInterface {
  /**
   * The manualCountry is only set in international addresses
   */
  manualCountry?: DropdownOptionSelection
  manualRegion?: DropdownOptionSelection
  manualCity?: DropdownOptionSelection
  manualQuarter?: ManualQuarter
  street?: string
  houseNumber?: string
  hideStreetAndStreetNumber?: boolean
  postcode?: string
  city?: string
  addressId?: string
  quarter?: string
  buildingType?: DropdownOptionSelection
  floor?: string
  totalFloorsNumber?: string // this is a String in CLB (noFloors)
  mainFloorSpace?: string
  separableSpace?: string
  totalFloorSpace?: string
  plotArea?: string
  livingSpace?: string
  price?: number
  priceType?: DropdownOptionSelection
  pricePeriodType?: DropdownOptionSelection
  room?: string
  additionalCosts?: number
  deposit?: string
  baseRent?: string
  freeUntil?: Date
  hasCourtage?: boolean
  courtage?: string
  courtageNote?: string
  adTitle?: string
  adDescription?: string
  adLocation?: string
  adOther?: string
  adFurnishingNote?: string
  isHomeStreetHomeEnabled?: boolean
  estateFeatures?: EstateFeatures
  interiorQuality?: InteriorQuality
  rentDurations?: RentDurations
  siteRecommendedUseTypes?: SiteRecommendedUseTypes
  freeFrom?: Date
  startRentalDate?: Date
  lanCables?: YesNoOrByAppointment
  airConditioning?: YesNoOrByAppointment
  flooringType?: FlooringTypeOption
  floorLoad?: string
  powerSupply?: string
  noParkingSpaces?: number
  parkingSpaceRent?: string
  parkingSpaceType?: ParkingSpaceTypeOption
  loadLifting?: boolean
  loadLiftingValue?: string
  goodsLift?: boolean
  goodsLiftLoad?: string
  crane?: boolean
  craneValue?: string
  usableArea?: string
  heightGarage?: string
  lengthGarage?: string
  widthGarage?: string
  salutation?: Salutation
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
  constructionCondition?: ConstructionConditionOption
  constructionLastModernization?: number
  constructionYear?: number
  legalConstructionYear?: string
  constructionEnergySource?: EnergySourceOption[]
  constructionHeatingType?: HeatingTypeOption
  energyCertificateAvailability?: EnergyCertificateAvailability
  energyCertificateCreationDate?: EnergyCertificateCreationDate
  buildingEnergyRatingType?: BuildingEnergyRatingType
  thermalCharacteristic?: string
  efficiencyClass?: EfficiencyClassOption
  consumptionContainsWarmWater?: boolean
  distanceToPT?: string
  distanceToMRS?: string
  distanceToFM?: string
  distanceToAirport?: string
  unknownConstructionYear?: boolean
  showWholeAddress?: boolean
  moveInDate?: Date
  petsAllowed?: YesOrNo | YesNoOrNegotiable
  noBathrooms?: string
  selfReport?: boolean
  solvency?: boolean
  rental?: boolean
  identification?: boolean
  income?: boolean
  intermediateRent?: boolean
  minimumTermOfLease?: string
  requestedGender?: Gender
  flatShareSize?: string
  smokingAllowed?: SmokingAllowed
  furnishing?: Furnishing
  numberOfRequestedFlatMates?: string
  ageOfRequestedFrom?: string
  ageOfRequestedTo?: string
  numberOfMaleFlatMates?: string
  numberOfFemaleFlatMates?: string
  ageOfFlatMatesFrom?: string
  ageOfFlatMatesTo?: string
  roomSize?: string
  rentSubsidy?: string
  grz?: string
  gfz?: string
  minRentalTime?: string
  maxRentalTime?: string
  maxNumberOfPersons?: string
  frontWindowLength?: string
  numberOfSeats?: string
  numberOfBeds?: string
  areaCoverage?: AreaCoverageOption
  constructibleAfter?: ConstructibleAfterOption
  locationClassificationType?: LocationClassificationTypeOption
  supplyType?: SupplyTypeOption
  usageType?: UsageTypeOption
  constructionPhase?: ConstructionPhaseOption
  objectName?: string
  rentalIncome?: string
  auxiliaryArea?: string
  published?: boolean
  addressEditable?: boolean
  noBedrooms?: string
  rented?: boolean
  summerResidencePractical?: boolean
  manualTotalRent?: string
  contactId?: number
  externalContactId?: string
  priceMultiplier?: number
  rentalIncomeActual?: string
  rentalIncomeTarget?: string
  industrialArea?: string
  pricePerSQM?: string
  serviceCharge?: string
  additionalArea?: string
  searchField1?: string
  searchField2?: string
  searchField3?: string
  conversionToLeadAllowed?: boolean

  externalId?: string
  groupNumber?: number
}

export type ContentStateProperty = Path<ContentStateInterface> | 'custom'

export type InsertionIdentification = {
  id: string
  unitId?: string
  buildingId?: string
  progressState?: string
  // OMA Id
  realEstateId?: number
  mapImages?: MapImages
  metaInformation?: {
    selectedMenuItem?: string
    entryMode?: EntryMode
  }
}

export type DropdownOptionSelection = { value: string | null; label: string | null }

export enum InteriorQuality {
  NO_INFORMATION = 'NO_INFORMATION',
  SIMPLE = 'SIMPLE',
  NORMAL = 'NORMAL',
  SOPHISTICATED = 'SOPHISTICATED',
  LUXURY = 'LUXURY',
}

export const InteriorQualityString = Object.keys(InteriorQuality).map(
  (key) => InteriorQuality[key as keyof typeof InteriorQuality]
)

export enum YesNoOrByAppointment {
  NO_INFORMATION = 'NO_INFORMATION',
  YES = 'YES',
  NO = 'NO',
  BY_APPOINTMENT = 'BY_APPOINTMENT',
}

export const YesNoOrByAppointmentString = Object.keys(YesNoOrByAppointment).map(
  (key) => YesNoOrByAppointment[key as keyof typeof YesNoOrByAppointment]
)

export enum YesOrNo {
  NO_INFORMATION = 'NO_INFORMATION',
  YES = 'YES',
  NO = 'NO',
}

export const YesOrNoString = Object.keys(YesOrNo).map((key) => YesOrNo[key as keyof typeof YesOrNo])

export enum YesNoOrNegotiable {
  NO_INFORMATION = 'NO_INFORMATION',
  YES = 'YES',
  NO = 'NO',
  NEGOTIABLE = 'NEGOTIABLE',
}

export const YesOrNoOrNegotiableString = Object.keys(YesNoOrNegotiable).map(
  (key) => YesNoOrNegotiable[key as keyof typeof YesNoOrNegotiable]
)

export enum Salutation {
  NO_SALUTATION = 'NO_SALUTATION',
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export enum Gender {
  MALE_OR_FEMALE = 'MALE_OR_FEMALE',
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export enum SmokingAllowed {
  NO_INFORMATION = 'NO_INFORMATION',
  ALLOWED = 'ALLOWED',
  UNWANTED = 'UNWANTED',
  OWN_ROOM_ONLY = 'OWN_ROOM_ONLY',
}

export enum Furnishing {
  NO_INFORMATION = 'NO_INFORMATION',
  FURNISHED = 'FURNISHED',
  UNFURNISHED = 'UNFURNISHED',
}

export const SalutationString = Object.keys(Salutation).map((key) => Salutation[key as keyof typeof Salutation])
export const GenderString = Object.keys(Gender).map((key) => Gender[key as keyof typeof Gender])
export const FurnishingString = Object.keys(Furnishing).map((key) => Furnishing[key as keyof typeof Furnishing])
export const SmokingAllowedString = Object.keys(SmokingAllowed).map(
  (key) => SmokingAllowed[key as keyof typeof SmokingAllowed]
)

export const allEstateFeatures = <const>[
  'basement',
  'hasCanteen',
  'handicappedAccessible',
  'highVoltage',
  'elevator',
  'kitchenComplete',
  'ramp',
  'liftingPlatform',
  'balconyTerrace',
  'certificateOfEligibilityNeeded',
  'equippedKitchen',
  'flatShareSuitable',
  'guestToilet',
  'sharedGarden',
  'dishwasher',
  'washingMachine',
  'bathHasWc',
  'bathHasShower',
  'bathHasTub',
  'internet',
  'tv',
  'summerResidencePractical',
  'lodgerFlat',
  'monument',
  'listed',
  'nonSmoker',
  'terrace',
  'shortTermConstructible',
  'buildingPermission',
  'demolition',
]

type AllEstateFeatures = (typeof allEstateFeatures)[number]
export type EstateFeatures = { [key in AllEstateFeatures]?: boolean }

export const allSiteRecommendedUseTypes = <const>[
  'noInformation',
  'futureDevelopmentLand',
  'twinHouse',
  'singleFamilyHouse',
  'garage',
  'garden',
  'noDevelopment',
  'apartmentBuilding',
  'orchard',
  'terraceHouse',
  'parkingSpace',
  'villa',
  'forrest',
  'farmland',
  'moorage',
  'office',
  'camping',
  'bigStore',
  'littleStore',
  'gastronomy',
  'business',
  'hotel',
  'industry',
  'smallBusiness',
  'stock',
  'carPark',
  'production',
]
type AllSiteRecommendedUseTypes = (typeof allSiteRecommendedUseTypes)[number]
export type SiteRecommendedUseTypes = { [key in AllSiteRecommendedUseTypes]?: boolean }

export type EstateFeaturesLanguages = Record<string, string>

export type TenantDocuments = {
  selfReport?: boolean
  solvency?: boolean
  rental?: boolean
  identification?: boolean
  income?: boolean
}

export type TenantDocumentsLanguages = Record<keyof TenantDocuments, TenantDocument>

export type TenantDocument = {
  translation: string
}

export type RentDurations = {
  weekly?: boolean
  monthly?: boolean
  yearly?: boolean
  longTerm?: boolean
}

export type RentDurationsLanguages = {
  weekly: string
  monthly: string
  yearly: string
  longTerm: string
}
export type SiteRecommendedUseTypesLanguages = Record<string, string>

export enum EnergyCertificateCreationDate {
  NO_INFORMATION = 'NO_INFORMATION',
  TILL_30_04_2014 = 'TILL_30_04_2014',
  FROM_01_05_2014 = 'FROM_01_05_2014',
}

export const EnergyCertificateCreationDateString = Object.keys(EnergyCertificateCreationDate).map(
  (key) => EnergyCertificateCreationDate[key as keyof typeof EnergyCertificateCreationDate]
)

export enum TransactionType {
  RENT = 'RENT',
  SELL = 'SELL',
}

export enum RealEstateCategory {
  APARTMENT = 'APARTMENT',
  HOUSE = 'HOUSE',
  SHORT_TERM_ACCOMMODATION = 'SHORT_TERM_ACCOMMODATION',
  PLOT = 'PLOT',
  GARAGE = 'GARAGE',
  OFFICE = 'OFFICE',
  STORE = 'STORE',
  GASTRONOMY = 'GASTRONOMY',
  PRODUCTION_HALL = 'PRODUCTION_HALL',
  INDUSTRIAL_PLOT = 'INDUSTRIAL_PLOT',
  FLAT_SHARE_ROOM = 'FLAT_SHARE_ROOM',
  INVESTMENT = 'INVESTMENT',
  HANGAR = 'HANGAR',
  SPECIAL_INDUSTRY = 'SPECIAL_INDUSTRY',
}

export enum SegmentationType {
  PROPERTY_OWNER = 'PROPERTY_OWNER',
  TENANT = 'TENANT',
  AGENT = 'AGENT',
}

export type FlowMetaData = {
  category: RealEstateCategory
  transaction: TransactionType
  segmentation: SegmentationType
}

export enum BuildingEnergyRatingType {
  NO_INFORMATION = 'NO_INFORMATION',
  // Bedarfsausweis
  ENERGY_REQUIRED = 'ENERGY_REQUIRED',
  // Verbrauchsausweis
  ENERGY_CONSUMPTION = 'ENERGY_CONSUMPTION',
}

export enum RealEstateFeatures {
  HANDICAPPED_ACCESSIBLE = 'handicappedAccessible',
  STEPLESS_ACCESSIBLE = 'steplessAccessible',
  HAS_CANTEEN = 'hasCanteen',
  BASEMENT = 'basement',
  KITCHEN_COMPLETE = 'kitchenComplete',
  ELEVATOR = 'elevator',
  HIGH_VOLTAGE = 'highVoltage',
  RAMP = 'ramp',
  LIFTING_PLATFORM = 'liftingPlatform',
  BALCONY_TERRACE = 'balconyTerrace',
  CERTIFICATE_OF_ELIGIBILITY_NEEDED = 'certificateOfEligibilityNeeded',
  EQUIPPED_KITCHEN = 'equippedKitchen',
  FLAT_SHARE_SUITABLE = 'flatShareSuitable',
  GUEST_TOILET = 'guestToilet',
  SHARED_GARDEN = 'sharedGarden',
  BATH_HAS_SHOWER = 'bathHasShower',
  BATH_HAS_WC = 'bathHasWc',
  BATH_HAS_BATHTUB = 'bathHasTub',
  TV_CABLE = 'tv',
  DISHWASHER = 'dishwasher',
  DSL = 'internet',
  WASHING_MACHINE = 'washingMachine',
  SUMMER_RESIDENCE_PRACTICAL = 'summerResidencePractical',
  LODGER_FLAT = 'lodgerFlat',
  MONUMENT = 'monument',
  // Same as monument, but used in Store real estate type
  LISTED = 'listed',
  NON_SMOKER = 'nonSmoker',
  TERRACE = 'terrace',
  SHORT_TERM_CONSTRUCTIBLE = 'shortTermConstructible',
  BUILDING_PERMISSION = 'buildingPermission',
  DEMOLITION = 'demolition',
  PERSON_ELEVATOR = 'personElevator',
}

export interface EstateFeatureProps {
  translationKey: string
  property: RealEstateFeatures
}

export enum RealEstateSiteRecommendedUseTypes {
  NO_INFORMATION = 'noInformation',
  FUTURE_DEVELOPMENT_LAND = 'futureDevelopmentLand',
  TWINHOUSE = 'twinHouse',
  SINGLE_FAMILY_HOUSE = 'singleFamilyHouse',
  GARAGE = 'garage',
  GARDEN = 'garden',
  NO_DEVELOPMENT = 'noDevelopment',
  APARTMENT_BUILDING = 'apartmentBuilding',
  ORCHARD = 'orchard',
  TERRACE_HOUSE = 'terraceHouse',
  PARKING_SPACE = 'parkingSpace',
  VILLA = 'villa',
  FORREST = 'forrest',
  LITTLE_STORE = 'littleStore',
  GASTRONOMY = 'gastronomy',
  BUSINESS = 'business',
  HOTEL = 'hotel',
  INDUSTRY = 'industry',
  SMALL_BUSINESS = 'smallBusiness',
  STOCK = 'stock',
  CAR_PARK = 'carPark',
  PRODUCTION = 'production',
  FARMLAND = 'farmland',
  MOORAGE = 'moorage',
  OFFICE = 'office',
  CAMPING = 'camping',
  BIG_STORE = 'bigStore',
}

export interface SiteRecommendedUseTypesProps {
  translationKey: string
  property: RealEstateSiteRecommendedUseTypes
}

export const BuildingEnergyRatingTypeString = Object.keys(BuildingEnergyRatingType).map(
  (key) => BuildingEnergyRatingType[key as keyof typeof BuildingEnergyRatingType]
)
export type DropdownValue = string | number | null
export type DropdownOption = {
  value: DropdownValue
  label: React.ReactNode
}

export type LocalizedDropdownOptions = {
  translationKeyGroup: string
  options: string[]
}

export type ManualGeoCoding = {
  countries: DropdownOption[]
  regions: DropdownOption[]
  cities: DropdownOption[]
  quarters: ManualGeoLoadResultArray
}

export type GeoCoordinates = {
  latitude: number
  longitude: number
  lambertEuCoordinatesX: number
  lambertEuCoordinatesY: number
}

export type DistanceToFieldType = {
  id: string
  fieldName: ContentStateProperty
  label: JSX.Element
  requiresMargin?: boolean
  maxLength: 2 | 3
}

export type Languages = 'de' | 'en'

export type GeoAutocompletionType = 'CITY' | 'STREET' | 'POSTCODE'
export type GeoAutocompletionResult = Promise<EntityObject[] | null | undefined>

export type ScreenProperty = {
  screen: ScreenName
  property: ContentStateProperty
}

export type GISResult = Promise<GeocodeObject>

export type Address = {
  street?: string
  houseNumber?: string
  postcode?: string
  city?: string
}

export type User = {
  contact: UserContactInfo
  contingentCustomer: boolean
  ssoId: number
}

export type UserContactInfo = {
  salutationType: Salutation
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
}

export type Contacts = Contact[]
export type ContactsApi = ContactApi[]

export type ContactAddress = {
  street?: string
  number?: string
  postcode?: string
  city?: string
}

export type ContactApi = {
  contactId: number
  salutationType?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
  faxNumber?: string
  cellPhoneNumber?: string
  address: ContactAddress
  title?: string
  defaultContact?: boolean
}

export type Contact = {
  contactId: number
  externalId?: string
  salutationType?: Salutation
  firstName?: string
  lastName?: string
  phoneNumber?: string
  email?: string
  faxNumber?: string
  cellPhoneNumber?: string
  address: ContactAddress
  title?: string
  defaultContact?: boolean
}

export type ManualQuarter = {
  value: string | null
  label: string | null
  geoCoordinates?: GeoCoordinates
}
export type ManualGeoLoadResultArray = ManualQuarter[]
export type ManualGeoLoadResult = Promise<ManualGeoLoadResultArray>
export type ManualGeoItem = { id: number; name: string; geoCoordinates?: GeoCoordinates }
export type ManualGeoItemArray = ManualGeoItem[]

export type Styles = { [key: string]: CSSProperties }

export enum AttachmentStatus {
  PERSISTED = 'PERSISTED',
  UPLOADING = 'UPLOADING',
  ERROR = 'ERROR',
}

export type AttachmentData = {
  state: AttachmentStatus
  attachmentType: AttachmentType
  attachment?: Attachment
  file?: UploadFile
}

export type AttachmentDataWithRequiredAttachment = AttachmentData & {
  state: AttachmentStatus.PERSISTED
  attachment: Attachment
}

export type Attachment = {
  id: string
  type?: AttachmentType
  url?: string
  linkUrl?: string
  source?: string
  size?: number
  title?: string
  bucketName?: string
  contentType?: string
  floorplan?: boolean
  picture?: boolean
  document?: boolean
  valid?: boolean
  file?: boolean
  link?: boolean
  video?: boolean
  videoId?: string
  offerApiId?: string
  offerRealEstateId?: string
}

export interface StreamingVideoUrl {
  format: string
  url: string
}

export interface VideoInfoProcess {
  offerApiId?: string
  title?: string
  videoId?: string
  videoInfo?: VideoInfo
}

export interface VideoInfo {
  duration: number
  processingProgress: number
  processingStatus: 'INCOMING' | 'PROCESSING' | 'SUCCESSFUL' | 'FAILED'
  teaserUrl?: string
  videoUrlList?: StreamingVideoUrl[]
}

export enum AttachmentType {
  PICTURE = 'PICTURE',
  DOCUMENT = 'DOCUMENT',
  LINK = 'LINK',
  VIDEO = 'VIDEO',
}

export interface InsertionEditorStateInterface {
  currentScreen: Screen
  currentScreenIndex: number
}

export type Screen = {
  id: string
  name: string
  trackingName: string
  translation: string
  defaultTranslation: string
}

export type StatusType = 'confirm' | 'info' | 'error'

export enum TooltipTypes {
  MOBILE = 'mobile',
  DESKTOP = 'desktop',
  LEVITATING = 'levitating',
}

export enum SaveType {
  SAVED = 'SAVED',
  NOT_SAVED = 'NOT_SAVED',
  FAILED_TO_SAVE = 'FAILED_TO_SAVE',
  SAVING = 'SAVING',
}

export type OnChangeValue =
  | string
  | number
  | undefined
  | boolean
  | DropdownOptionSelection
  | Date
  | DropdownOptionSelection[]
  | DropdownValue[]
  | DropdownOption

export type LocationMapGeolocation = { latitude: number; longitude: number; isManual: boolean }

export interface InsertionEditorMethods {
  onNavigate: (screen: Screen) => void
  onChange: OnFieldChangeType
  doResetInsertionData: (clearingUpOf: ScreenProperty[]) => void
  doCloseMultimediaStatusMessage?: () => { type: string }
}

export interface InsertionEditorElements {
  insertionEditorState: {
    insertionData: ContentStateInterface
    validationResult: ValidationResult
  }
  insertionEditorMethods: InsertionEditorMethods
}

export type OnFieldChangeType = (screenProperty: ScreenProperty, value: OnChangeValue) => void

export interface FieldProps<T extends AbstractScreenValidationResult> {
  flowInformation?: FlowInformation
  insertionData: ContentStateInterface
  validationMessages?: T
  screenName: ScreenName
  screenId?: string
  wrapperClassName?: string
  doReset?: (clearingUpOf: ScreenProperty[]) => void
  onChange: OnFieldChangeType
}

export interface DescriptionFieldProps<T extends AbstractScreenValidationResult> extends FieldProps<T> {
  showTitleSubtitle?: boolean
  showDescriptionSubtitle?: boolean
  showLocationSubtitle?: boolean
  onHandleTitleChange?: () => false | { type: string }
  onHandleDescriptionChange?: () => false | { type: string }
  onHandleLocationChange?: () => false | { type: string }
  initialPlaceholder?: string[]
}

export type Base64 = string

export type manualAddressType = { manualAddress: { manualGeoCoding?: ManualGeoCoding } } | undefined

export type FloorAndTotalFloorWrapperType = 'full-line' | 'left' | 'right'

export type VideoTicket = {
  uploadUrl: string
  validUntil: string
  videoId: string
}

export type ObjectParams = {
  type: RealEstateCategory
  transaction: TransactionType
  segment: SegmentationType
  buildingId?: string
  unitId?: string
  entryMode?: EntryMode
}
